import React, { useState, useRef, useEffect } from "react";
import { Mic, ScanBarcode, RefreshCw, Star, ArrowLeft, Edit2, Plus, AlertCircle, MicOff, Camera, CameraOff } from "lucide-react";
import { toast } from "sonner";
import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";
import { useBarcodeScan } from "@/hooks/useBarcodeScan";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const STORAGE_TECH_KEY = "gotparts_tech_id";

// ---------------------------------------------------------------- small components

function TechSelector({ techs, selectedId, onSelect }) {
  return (
    <div className="w-full">
      <label className="block text-sm font-semibold text-white/70 mb-1">Technician</label>
      <select
        value={selectedId || ""}
        onChange={(e) => onSelect(e.target.value)}
        className="w-full bg-white/10 border border-white/20 text-white rounded-xl px-4 py-3 text-base font-medium appearance-none"
      >
        <option value="">— Select technician —</option>
        {techs.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name} ({t.region})
          </option>
        ))}
      </select>
    </div>
  );
}

function BigButton({ icon: Icon, label, sub, onClick, variant = "default", disabled }) {
  const base =
    "flex flex-col items-center justify-center gap-2 rounded-2xl font-bold text-center transition-all active:scale-95 select-none p-4";
  const variants = {
    default: "bg-white/10 hover:bg-white/20 text-white border border-white/20",
    primary: "bg-orange hover:bg-orange/90 text-white",
    outline: "bg-transparent hover:bg-white/10 text-white border-2 border-white/40",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${variants[variant]} ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
      style={{ minHeight: "7rem" }}
    >
      <Icon className="w-10 h-10" strokeWidth={1.5} />
      <span className="text-lg leading-tight">{label}</span>
      {sub && <span className="text-xs font-normal text-white/60">{sub}</span>}
    </button>
  );
}

function PartCard({ part, onAdd, queryLogId }) {
  const [added, setAdded] = useState(false);

  async function handleAdd() {
    if (added) return;
    // Log the choice
    if (queryLogId) {
      try {
        await api.post("/api/tech/log-choice", { query_log_id: queryLogId, chosen_item_id: part.id });
      } catch {}
    }
    setAdded(true);
    onAdd(part);
  }

  return (
    <div className="flex gap-3 bg-white/10 rounded-2xl p-3 items-start border border-white/10">
      <div className="w-16 h-16 shrink-0 rounded-xl bg-white/5 overflow-hidden flex items-center justify-center">
        {part.main_image ? (
          <img src={part.main_image} alt="" className="w-full h-full object-cover" />
        ) : (
          <ScanBarcode className="w-8 h-8 text-white/20" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-semibold text-sm leading-snug line-clamp-2">{part.name}</p>
        <p className="text-white/60 text-xs mt-0.5">{part.brand} · {part.sku}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-orange font-bold text-sm">{part.price_display}</span>
          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${part.is_in_stock ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"}`}>
            {part.is_in_stock ? "In Stock" : "Out of Stock"}
          </span>
        </div>
      </div>
      <button
        onClick={handleAdd}
        className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center font-bold text-white transition-all ${added ? "bg-green-500" : "bg-orange active:scale-90"}`}
      >
        {added ? "✓" : <Plus className="w-6 h-6" />}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------- states

const STATES = {
  HOME: "home",
  RECORDING: "recording",
  PROCESSING: "processing",
  RESULTS: "results",
  SCANNING: "scanning",
  FREQUENT: "frequent",
  LAST_ORDER: "last-order",
};

// ---------------------------------------------------------------- main

export default function Tech() {
  const [screen, setScreen] = useState(STATES.HOME);
  const [techs, setTechs] = useState([]);
  const [techId, setTechId] = useState(() => localStorage.getItem(STORAGE_TECH_KEY) || "");
  const [results, setResults] = useState([]);
  const [transcript, setTranscript] = useState("");
  const [queryLogId, setQueryLogId] = useState(null);
  const [editingTranscript, setEditingTranscript] = useState(false);
  const [editedTranscript, setEditedTranscript] = useState("");
  const [cart, setCart] = useState([]);

  const videoRef = useRef(null);
  const voice = useVoiceRecorder();
  const scan = useBarcodeScan();

  useEffect(() => {
    api.get("/api/tech/technicians")
      .then((d) => setTechs(d.data.items || []))
      .catch(() => {});
  }, []);

  function selectTech(id) {
    setTechId(id);
    localStorage.setItem(STORAGE_TECH_KEY, id);
  }

  // ---------------------------------------------------------------- voice flow
  async function handleVoiceTap() {
    if (voice.state === "recording") {
      voice.stop();
      return;
    }
    if (voice.permissionDenied) {
      toast.error("Microphone access denied. Use the search box instead.");
      return;
    }
    setScreen(STATES.RECORDING);
    await voice.start(async (blob) => {
      setScreen(STATES.PROCESSING);
      const form = new FormData();
      form.append("audio", blob, "voice.webm");
      if (techId) form.append("technician_id", techId);
      try {
        const res = await fetch(`${BACKEND}/api/tech/voice`, { method: "POST", body: form });
        const data = await res.json();
        if (data.error) {
          toast.error("Transcription unavailable — please type your search.");
          setScreen(STATES.HOME);
          return;
        }
        setTranscript(data.transcript || "");
        setEditedTranscript(data.transcript || "");
        setResults(data.results || []);
        setQueryLogId(data.query_log_id || null);
        setScreen(STATES.RESULTS);
      } catch {
        toast.error("Connection error. Please try again.");
        setScreen(STATES.HOME);
      }
    });
  }

  async function reSearchWithEdit() {
    setEditingTranscript(false);
    setScreen(STATES.PROCESSING);
    try {
      const res = await api.post("/api/tech/search", {
        query: editedTranscript,
        technician_id: techId || null,
      });
      setTranscript(editedTranscript);
      setResults(res.data.results || []);
      setQueryLogId(res.data.query_log_id || null);
    } catch {
      toast.error("Search failed.");
    } finally {
      setScreen(STATES.RESULTS);
    }
  }

  // ---------------------------------------------------------------- scan flow
  async function handleScanTap() {
    if (scan.cameraUnavailable) {
      toast.error("Camera not available. Enter a part number manually.");
      return;
    }
    setScreen(STATES.SCANNING);
  }

  useEffect(() => {
    if (screen === STATES.SCANNING && videoRef.current) {
      scan.start(videoRef.current, async (barcode) => {
        setScreen(STATES.PROCESSING);
        try {
          const res = await api.post("/api/tech/scan", { barcode, technician_id: techId || null });
          setResults(res.data.results || []);
          setTranscript(barcode);
          setEditedTranscript(barcode);
          setQueryLogId(null);
          setScreen(STATES.RESULTS);
        } catch {
          toast.error("Scan lookup failed.");
          setScreen(STATES.HOME);
        }
      });
    }
  }, [screen]);

  // ---------------------------------------------------------------- frequent / last order
  async function handleFrequent() {
    if (!techId) { toast.error("Select a technician first."); return; }
    setScreen(STATES.PROCESSING);
    try {
      const res = await api.get(`/api/tech/frequent/${techId}`);
      setResults(res.data.items || []);
      setTranscript("My frequent items");
      setQueryLogId(null);
      setScreen(STATES.FREQUENT);
    } catch {
      toast.error("Could not load frequent items."); setScreen(STATES.HOME);
    }
  }

  async function handleLastOrder() {
    if (!techId) { toast.error("Select a technician first."); return; }
    setScreen(STATES.PROCESSING);
    try {
      const res = await api.get(`/api/tech/last-order/${techId}`);
      setResults(res.data.items || []);
      setTranscript(`Last order · ref: ${res.data.source_ref || "—"}`);
      setQueryLogId(null);
      setScreen(STATES.LAST_ORDER);
    } catch {
      toast.error("Could not load last order."); setScreen(STATES.HOME);
    }
  }

  function addToCart(part) {
    setCart((c) => {
      const existing = c.find((x) => x.id === part.id);
      if (existing) return c.map((x) => x.id === part.id ? { ...x, qty: x.qty + 1 } : x);
      return [...c, { ...part, qty: 1 }];
    });
    toast.success(`${part.name} added to cart`);
  }

  function goHome() {
    scan.stop();
    voice.stop();
    setScreen(STATES.HOME);
  }

  // ---------------------------------------------------------------- render helpers

  function renderHeader() {
    return (
      <header className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        {screen !== STATES.HOME ? (
          <button onClick={goHome} className="p-2 rounded-xl text-white/70 hover:text-white hover:bg-white/10">
            <ArrowLeft className="w-6 h-6" />
          </button>
        ) : (
          <div className="w-10" />
        )}
        <span className="text-white font-bold text-lg tracking-tight">TECH PORTAL</span>
        {cart.length > 0 ? (
          <button
            onClick={() => window.location.href = "/cart"}
            className="relative p-2 bg-orange rounded-xl text-white font-bold text-sm px-3"
          >
            Cart {cart.length}
          </button>
        ) : (
          <div className="w-16" />
        )}
      </header>
    );
  }

  function renderResults(showQty = false) {
    return (
      <div className="flex-1 overflow-y-auto px-4 pb-6 space-y-3">
        {/* Transcript bar */}
        <div className="bg-white/5 rounded-2xl p-3 border border-white/10">
          {editingTranscript ? (
            <div className="flex gap-2">
              <input
                autoFocus
                value={editedTranscript}
                onChange={(e) => setEditedTranscript(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") reSearchWithEdit(); }}
                className="flex-1 bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white text-sm"
              />
              <button onClick={reSearchWithEdit} className="bg-orange text-white rounded-xl px-4 py-2 text-sm font-bold">
                Search
              </button>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-2">
              <p className="text-white/80 text-sm italic flex-1">"{transcript}"</p>
              <button onClick={() => setEditingTranscript(true)} className="shrink-0 p-1 text-white/40 hover:text-white">
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {results.length === 0 ? (
          <div className="text-center py-12 text-white/40">
            <AlertCircle className="w-10 h-10 mx-auto mb-2" />
            No matches found. Try editing the search above.
          </div>
        ) : (
          results.map((part) => (
            <PartCard
              key={part.id}
              part={part}
              onAdd={addToCart}
              queryLogId={queryLogId}
            />
          ))
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------- screens

  if (screen === STATES.RECORDING) {
    return (
      <div className="min-h-screen bg-graphite flex flex-col items-center justify-center gap-6 px-6">
        {renderHeader()}
        <div className="flex-1 flex flex-col items-center justify-center gap-6">
          <div className="w-28 h-28 rounded-full bg-red-500/20 border-4 border-red-400 flex items-center justify-center animate-pulse">
            <Mic className="w-14 h-14 text-red-300" />
          </div>
          <p className="text-white text-2xl font-bold">{voice.secondsLeft}s</p>
          <p className="text-white/60 text-center">Speak now — tap to stop early</p>
          <button onClick={() => voice.stop()} className="bg-red-500 text-white rounded-2xl px-8 py-4 text-lg font-bold">
            Stop Recording
          </button>
        </div>
      </div>
    );
  }

  if (screen === STATES.PROCESSING) {
    return (
      <div className="min-h-screen bg-graphite flex flex-col items-center justify-center gap-4">
        <div className="w-16 h-16 rounded-full border-4 border-orange border-t-transparent animate-spin" />
        <p className="text-white text-lg font-semibold">Searching…</p>
      </div>
    );
  }

  if (screen === STATES.SCANNING) {
    return (
      <div className="min-h-screen bg-graphite flex flex-col">
        {renderHeader()}
        <div className="flex-1 relative">
          <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-64 h-48 border-4 border-orange rounded-2xl opacity-80" />
          </div>
          <div className="absolute bottom-8 left-0 right-0 flex justify-center">
            <button onClick={goHome} className="bg-white/10 border border-white/20 text-white rounded-2xl px-8 py-4 text-lg font-bold">
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (screen === STATES.RESULTS || screen === STATES.FREQUENT || screen === STATES.LAST_ORDER) {
    return (
      <div className="min-h-screen bg-graphite flex flex-col">
        {renderHeader()}
        {renderResults()}
      </div>
    );
  }

  // HOME screen
  return (
    <div className="min-h-screen bg-graphite flex flex-col">
      {renderHeader()}

      <div className="px-4 py-4">
        <TechSelector techs={techs} selectedId={techId} onSelect={selectTech} />
      </div>

      <div className="flex-1 px-4 pb-8 grid grid-cols-2 gap-3 content-start">
        <BigButton
          icon={voice.permissionDenied ? MicOff : Mic}
          label="Speak"
          sub={voice.permissionDenied ? "Mic blocked" : "English / Spanish"}
          onClick={handleVoiceTap}
          variant="primary"
          disabled={voice.state === "recording"}
        />
        <BigButton
          icon={scan.cameraUnavailable ? CameraOff : ScanBarcode}
          label="Scan"
          sub={scan.cameraUnavailable ? "No camera" : "Barcode / QR"}
          onClick={handleScanTap}
          variant="default"
        />
        <BigButton
          icon={RefreshCw}
          label="Repeat Last Order"
          onClick={handleLastOrder}
          variant="outline"
        />
        <BigButton
          icon={Star}
          label="My Frequent Items"
          onClick={handleFrequent}
          variant="outline"
        />
      </div>

      {cart.length > 0 && (
        <div className="px-4 pb-6">
          <a
            href="/cart"
            className="block w-full bg-orange text-white text-center font-bold text-lg rounded-2xl py-4"
          >
            View Cart ({cart.length} item{cart.length !== 1 ? "s" : ""})
          </a>
        </div>
      )}

      {/* Manual text search fallback */}
      <div className="px-4 pb-6">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const q = e.target.q.value.trim();
            if (!q) return;
            setScreen(STATES.PROCESSING);
            try {
              const res = await api.post("/api/tech/search", { query: q, technician_id: techId || null });
              setTranscript(q);
              setEditedTranscript(q);
              setResults(res.data.results || []);
              setQueryLogId(res.data.query_log_id || null);
              setScreen(STATES.RESULTS);
            } catch {
              toast.error("Search failed."); setScreen(STATES.HOME);
            }
          }}
          className="flex gap-2"
        >
          <input
            name="q"
            placeholder="Or type a part name…"
            className="flex-1 bg-white/10 border border-white/20 text-white placeholder-white/30 rounded-xl px-4 py-3 text-base"
          />
          <button type="submit" className="bg-white/10 border border-white/20 text-white rounded-xl px-5 py-3 font-bold">
            Go
          </button>
        </form>
      </div>
    </div>
  );
}
